import React, {
  memo,
  ChangeEvent,
  FormEvent,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import isEqual from 'lodash/isEqual'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { string } from 'yup'
import { ICustomerContact, CUSTOMER_CONTACT_PURPOSE, IStop } from 'apps/fleet-planner/types'
import {
  updateFacilityThunk,
  createNewFacilitySuccess,
} from 'apps/fleet-planner/utils/actions/facilities'
import {
  FacilityPurpose,
  createNewFacility,
  ICreateNewFacilityRequest,
  IFacilityAddress,
  IFacility,
} from 'apps/fleet-planner/utils/api'
import validateCreateFacility, {
  CreateFacilitySchema,
} from 'apps/fleet-planner/utils/createFacilityValidator'
import { ErrorsOutput } from 'apps/fleet-planner/utils/schemaValidator'
import { recentFacilitiesStateSelector } from 'apps/fleet-planner/utils/selectors/facilities'
import { TextInput } from 'components/atoms'
import { Button, PhoneInput } from 'transfix-ui'

export const generateAddressObject = ({
  addressLine1 = '',
  addressLine2 = '',
  addressee = '',
  countryCode = 'US',
  locality = '',
  postalCode = '',
  subdivisionCode = '',
}: CreateFacilitySchema): IFacilityAddress => ({
  addressee,
  addressLine1,
  addressLine2,
  postalCode,
  subdivisionCode,
  locality,
  countryCode,

  // TODO: Verify that this is the default behavior
  purpose: FacilityPurpose.facilityPrimary,
})

export const generateContactsObject = ({
  email = '',
  name = '',
  phone = '',
  phoneExt = '',
}: CreateFacilitySchema): ICustomerContact => ({
  name,
  email,
  phone,
  phoneExt,
  purpose: CUSTOMER_CONTACT_PURPOSE.FACILITY_PRIMARY,
})

export const getUpdatedStop = (
  {
    address,
    facility,
    facilityContact,
  }: {
    address?: Partial<IFacilityAddress>
    facilityContact?: Partial<ICustomerContact>
    facility?: {
      name?: string
      id?: string
    }
  },
  stop: Partial<IStop> = {}
): Partial<IStop> => {
  const updatedStop = { ...stop }

  // Contact
  if (facilityContact && Object.keys(facilityContact).length) {
    updatedStop.facilityContact = {
      ...stop.facilityContact,
      ...(facilityContact as ICustomerContact),
    }
  }

  // Facility
  if (facility && Object.keys(facility).length) {
    updatedStop.facility = {
      ...stop.facility,
      ...facility,
    }
  }

  // Address
  if (address && Object.keys(address).length) {
    updatedStop.address = {
      ...stop.address,
      ...address,
    }
  }

  // Locality
  if (address?.locality) {
    updatedStop.city = address.locality
  }

  // State
  if (address?.subdivisionCode) {
    updatedStop.stateCode = address.subdivisionCode
  }

  return updatedStop
}

export const generateFacilityRequest = (
  facilityAddress: CreateFacilitySchema
): ICreateNewFacilityRequest => {
  const { addressee: name } = facilityAddress
  const facility: Omit<IFacility, 'id'> = {
    name: `${name}`,
    addresses: [generateAddressObject(facilityAddress)],
    contacts: [],
  }

  // If there's a valid contact, add it
  if (facilityAddress.email || facilityAddress.name || facilityAddress.phone) {
    facility.contacts = [generateContactsObject(facilityAddress)]
  }

  return {
    facility,
  }
}

interface IAddNewFacilityProps {
  changeHandler: (change: CreateFacilitySchema) => void
  addFacility: (facility: Partial<IFacility>) => void

  /**
   * Optionally send back a facility to replace current edits with `cancelFacility`
   */
  cancelFacility: (facility?: IFacility) => void
  errors?: {
    [key: string]: string
  }
  facilityAddress: CreateFacilitySchema
  facilityContact?: ICustomerContact
  facilityId?: string
  isEditing?: boolean
  isNewFacility?: boolean
}

const defaultNextFacilityAddressState = {
  id: undefined,
  contactId: undefined,
  addressableId: undefined,
  addressee: '',
  addressLine1: '',
  addressLine2: '',
  locality: '',
  subdivisionCode: '',
  postalCode: '',
  name: '',
  email: '',
  phone: '',
  phoneExt: '',
  countryCode: 'US',
}

const AddNewFacilityForm = memo<IAddNewFacilityProps>(
  ({ addFacility, cancelFacility, facilityAddress = {}, errors, isNewFacility, facilityId }) => {
    const { facilities } = useSelector(recentFacilitiesStateSelector)
    const [nextFacilityAddress, setNextFacilityAddress] = useState<Partial<CreateFacilitySchema>>(
      defaultNextFacilityAddressState
    )
    const [validationErrors, setValidationErrors] = useState<ErrorsOutput<CreateFacilitySchema>>({})
    const dispatch = useDispatch()
    /**
     * Executes when input value changes on SuggestLocation
     */
    const locationInputChangeHandler = useCallback(
      ({ city, postalCode: zip, state }: ILocationSuggestion): void => {
        setNextFacilityAddress(prevAddress => ({
          ...prevAddress,
          locality: city ?? '',
          subdivisionCode: state ?? '',
          postalCode: zip ?? '',
        }))
      },
      []
    )

    /**
     * If a user is accidentally re-adding, allow them to select previous Facility
     */
    const suggestedFacility = useMemo(() => {
      const newName = nextFacilityAddress?.addressee?.toLowerCase().trim()
      return facilities.find(f => {
        // Should have same name but not actually be the same facility,
        // which will occur when updating a facility
        return f.name.toLowerCase().trim() === newName && f.id !== nextFacilityAddress.id
      })
    }, [facilities, nextFacilityAddress])

    /**
     * When this component mounts, or when `facilityAddress`
     * changes, update the local form state
     */
    useEffect(() => {
      setNextFacilityAddress(prevAddress => ({
        ...prevAddress,
        ...facilityAddress,
      }))
    }, [facilityAddress])

    /**
     * When creating a facility, either phone or email
     * is required, but not both
     */
    const getCustomRequiredFields = useCallback((): { email: boolean; phone: boolean } => {
      const { email: userEmail, name, phone } = nextFacilityAddress || {}
      return {
        phone: !!(name && !userEmail),
        email: !!(name && !phone),
      }
    }, [nextFacilityAddress])

    const validateInputFields = useCallback(() => {
      const requiredMessage = 'This field is required.'
      const emailMessage = 'Must be a valid email.'
      const requiredFields = getCustomRequiredFields()

      const conditionalValidations = {
        name: string(),
        phone: requiredFields.phone ? string().required(requiredMessage) : string().notRequired(),
        email: requiredFields.email
          ? string().email(emailMessage).required(requiredMessage)
          : string().email(emailMessage).notRequired(),
        addressee: isNewFacility
          ? string().test(
              'is-unique-name',
              'A facility with this name already exists.',
              (addresseeVal: string) => {
                const newName = addresseeVal.toLowerCase().trim()

                // If the facility has no `addressableId` then it means it's new
                return (
                  !nextFacilityAddress.addressableId &&
                  !facilities.find(f => f.name.toLowerCase().trim() === newName)
                )
              }
            )
          : string(),
      }

      const { errors: inputErrors } = validateCreateFacility.validate(
        nextFacilityAddress,
        conditionalValidations
      )

      if (Object.keys({ ...inputErrors }).length) {
        setValidationErrors({ ...inputErrors })
        return false
      }

      setValidationErrors({})
      return true
    }, [isNewFacility, nextFacilityAddress, facilities, getCustomRequiredFields])

    /**
     * Executes when input values change in form
     */
    const inputChangeHandler = useCallback(
      (e: ChangeEvent<HTMLInputElement>): void => {
        setNextFacilityAddress(prevAddress => ({
          ...prevAddress,
          [e.target.id]: e.target.value,
        }))

        if (Object.keys(validationErrors).length) {
          validateInputFields()
        }
      },
      [validateInputFields, validationErrors]
    )

    /**
     * Memoized calculated condition to autofocus on input
     */
    const shouldAutoFocusFirstInput = useMemo(() => !nextFacilityAddress.addressLine1, [
      nextFacilityAddress.addressLine1,
    ])

    /**
     * Sends POST request to /facilities with a new Facility object
     */
    const saveFacilityHandler = async () => {
      const req = generateFacilityRequest(nextFacilityAddress)

      try {
        const { facility } = await createNewFacility(req)
        addFacility(facility)
        dispatch(createNewFacilitySuccess(facility))
      } catch (e) {
        //
      }
    }

    /**
     * Update an existing facility
     */
    const updateFacilityHandler = async () => {
      if (!facilityId) return undefined
      const matchingFacility = facilities.filter(f => f.id === facilityId)[0] || {}
      const reduxUpdate: any = {}

      // Update address
      const nextAddress = generateAddressObject(nextFacilityAddress)
      const prevAddress = generateAddressObject(facilityAddress)
      const { addresses = [] } = matchingFacility || {}
      const { id: addressId } = addresses[0] || {}
      if (!isEqual(nextAddress, prevAddress)) {
        reduxUpdate.address = {
          ...nextAddress,
          id: addressId,
        }
      }

      // Update contacts
      const nextContact = generateContactsObject(nextFacilityAddress)
      const prevContact = generateContactsObject(facilityAddress)
      const { contacts = [] } = matchingFacility || {}
      const { id: contactId } = contacts[0] || {}

      // Only update if object is new, has an id, and has a valid name
      if (!isEqual(nextContact, prevContact) && !!nextContact.name) {
        reduxUpdate.contact = {
          ...nextContact,
          id: contactId,
        }
      }

      // Update facility
      if (nextFacilityAddress.addressee !== facilityAddress.addressee) {
        reduxUpdate.facility = {
          name: nextFacilityAddress.addressee,
          id: facilityId,
        }
      }

      dispatch(
        updateFacilityThunk(facilityId, addressId, `${contactId}`, {
          ...reduxUpdate,
        })
      )

      let facilityUpdate: IFacility = {} as IFacility

      if (reduxUpdate?.facility) {
        facilityUpdate = reduxUpdate?.facility
      }

      if (reduxUpdate?.address) {
        facilityUpdate.addresses = [reduxUpdate?.address]
      }

      if (reduxUpdate?.contact) {
        facilityUpdate.contacts = [reduxUpdate?.contact]
      }

      return addFacility({
        ...matchingFacility,
        ...facilityUpdate,
      })
    }

    const submitForm = () => {
      const isValid = validateInputFields()
      if (!isValid) return undefined

      if (!isNewFacility) {
        return updateFacilityHandler()
      }

      return saveFacilityHandler()
    }

    const formSubmitHandler = (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault()
      submitForm()
    }

    const cancelFacilityHandler = () => {
      cancelFacility()
    }

    const headerClasses = classNames('body-medium', { mb4: isNewFacility, mb2: !isNewFacility })

    const locationValue: ILocationSuggestion = useMemo(
      () => ({
        city: nextFacilityAddress.locality,
        state: nextFacilityAddress.subdivisionCode,
      }),
      [nextFacilityAddress.subdivisionCode, nextFacilityAddress.locality]
    )

    const suggestedFacilityMessage = useMemo(() => {
      return suggestedFacility ? 'This facility name is already in use.' : undefined
    }, [suggestedFacility])

    return (
      <div className='pa4 bg-secondary col-md-12 rounded' id='add-new-facility'>
        <div className={headerClasses}>{isNewFacility ? 'New' : 'Edit'} Facility</div>
        {!isNewFacility && (
          <div className='text-body mb4'>
            Changes to this saved facility won&rsquo;t be applied to any existing loads
          </div>
        )}
        <form
          onSubmit={formSubmitHandler}
          name='add-new-facility-form'
          id='add-new-facility-form'
          className='add-new-facility-form'>
          {/* Facility Name */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-name'>
              <TextInput
                clearable={false}
                autoFocus={shouldAutoFocusFirstInput}
                autoComplete='off'
                dataTest='facility-name-input'
                id='addressee'
                invalid={!!validationErrors?.addressee || !!suggestedFacilityMessage}
                label='Facility Name'
                name='addressee'
                onChange={inputChangeHandler}
                placeholder='Houston Warehouse'
                required
                validationMessage={validationErrors?.addressee || suggestedFacilityMessage}
                value={nextFacilityAddress.addressee || ''}
              />
            </div>
          </div>

          {/* Address line 1 */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-address-line-1'>
              <TextInput
                clearable={false}
                autoComplete='off'
                dataTest='address-line-1-input'
                id='addressLine1'
                invalid={!!validationErrors?.addressLine1}
                label='Address Line 1'
                onChange={inputChangeHandler}
                required
                type='text'
                validationMessage={validationErrors?.addressLine1}
                value={nextFacilityAddress.addressLine1 || ''}
              />
            </div>
          </div>

          {/* Address line 2 */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-address-line-2'>
              <TextInput
                clearable={false}
                autoComplete='off'
                dataTest='address-line-2-input'
                id='addressLine2'
                invalid={!!validationErrors?.addressLine2}
                label='Address Line 2'
                onChange={inputChangeHandler}
                placeholder='Apt 5'
                type='text'
                validationMessage={validationErrors?.addressLine2}
                value={nextFacilityAddress.addressLine2 || ''}
              />
            </div>
          </div>

          {/* City */}
          <div className='row'>
            <div className='col-md-9' data-test='facility-city'>
              <SuggestLocation
                dataTest='facility-city-input'
                id='city'
                label='City'
                resultTypes={['City', 'State', 'PostalCode']}
                clearable={false}
                /**
                 * Executes when input value changes on SuggestLocation
                 */
                onChange={locationInputChangeHandler}
                placeholder='Choose location'
                required
                errorMessage={validationErrors?.locality || errors?.subdivisionCode || ''}
                value={locationValue}
              />
            </div>
            <div className='col-md-3' data-test='truck-number'>
              <TextInput
                clearable={false}
                autoComplete='off'
                dataTest='facility-postalCode'
                id='postalCode'
                invalid={!!validationErrors?.postalCode}
                label='Zip Code'
                maxLength={5}
                name='facility.location.postalCode'
                onChange={inputChangeHandler}
                placeholder='e.g 76473'
                validationMessage={validationErrors?.postalCode}
                value={nextFacilityAddress.postalCode || ''}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-12'>
              <div className='mt2 b'>Contact Information</div>
            </div>
          </div>

          {/* Facility Contact Name */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-contact-name'>
              <TextInput
                clearable={false}
                autoComplete='off'
                dataTest='facility-contact-name-input'
                id='name'
                invalid={!!validationErrors?.name}
                label='Facility Contact Name'
                name='name'
                onChange={inputChangeHandler}
                placeholder='e.g. Johnny Appleseed'
                validationMessage={validationErrors?.name}
                value={nextFacilityAddress.name || ''}
              />
            </div>
          </div>

          {/* Email */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-email'>
              <TextInput
                className='fs-mask'
                clearable={false}
                autoComplete='off'
                dataTest='facility-email-input'
                id='email'
                invalid={!!validationErrors?.email}
                label='Email'
                name='email'
                onChange={inputChangeHandler}
                placeholder='Enter email address'
                required={!!(nextFacilityAddress.name && !nextFacilityAddress.phone)}
                validationMessage={validationErrors?.email}
                value={nextFacilityAddress.email || ''}
              />
            </div>
          </div>

          {/* Phone */}
          <div className='row'>
            <div className='col-md-4' data-test='facility-phone'>
              <PhoneInput
                className='fs-mask'
                autoComplete='off'
                dataTest='phone-input'
                id='phone'
                invalid={!!validationErrors?.phone}
                label='Phone'
                onChange={inputChangeHandler}
                placeholder='(000) 000-0000'
                required={!!(nextFacilityAddress.name && !nextFacilityAddress.email)}
                validationMessage={validationErrors?.phone}
                value={nextFacilityAddress.phone || ''}
              />
            </div>
            <div className='col-md-4' data-test='truck-number'>
              <TextInput
                clearable={false}
                autoComplete='off'
                dataTest='facility-phone-phoneExt-input'
                id='phoneExt'
                invalid={!!validationErrors?.phoneExt}
                label='Ext'
                name='facility-phone-phoneExt'
                onChange={inputChangeHandler}
                value={nextFacilityAddress.phoneExt || ''}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-12 mb0'>
              <div className='mt2 tr'>
                <Button
                  color='tertiary'
                  size='large'
                  onClick={cancelFacilityHandler}
                  id='facility-cancel-button'>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  id='facility-save-button'
                  onClick={submitForm}
                  data-test='save-new-truck'>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    )
  }
)

export default AddNewFacilityForm
